import { formatNumber } from "@narmi/design_system";
import { format, parseISO } from "date-fns";

// Backend sends two date shapes through the receipts payloads:
// - `transaction_date` on a Receipt: date-only ("2026-05-10")
// - `datetime` on a candidate transaction: full ISO datetime
// Stripping the time portion before parsing keeps the rendered day stable for
// users outside UTC — a midnight-UTC datetime would otherwise display as the
// previous day in negative-offset timezones.
//
// The year is shown only when the date is NOT in the current calendar year, so
// "Mar 14" always reads as this year and an older receipt reads "Mar 14, 2025"
// instead of being mistaken for the current year.
export const formatReceiptDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    const dateOnly = iso.split("T")[0];
    const date = parseISO(dateOnly);
    const isCurrentYear = date.getFullYear() === new Date().getFullYear();
    return format(date, isCurrentYear ? "MMM d" : "MMM d, yyyy");
  } catch {
    return iso;
  }
};

// Amounts arrive as decimal strings from the backend. Delegate the locale
// + currency rendering to NDS `formatNumber` once we've coerced to Number.
// Returns "" for nullish / NaN input so call sites can use `||` for fallbacks.
//
// Rendered as a magnitude: transaction amounts carry a signed ledger value
// (a debit on a deposit account is negative), but these displays are for a
// person reading "how much", not accounting — so the sign is dropped. The
// receipt-vs-transaction direction is conveyed separately by the delta chip.
export const formatReceiptAmount = (raw: string | null | undefined): string => {
  if (raw === null || raw === undefined || raw === "") return "";
  const value = Number(raw);
  return Number.isNaN(value) ? "" : formatNumber(Math.abs(value), "currency");
};

export interface DeltaDisplay {
  display: string;
  kind: "success" | "warn";
}

// Signed delta between a receipt and its matched/suggested transaction,
// formatted for a chip. Returns null for a missing or exactly-zero delta so
// callers hide the chip entirely — an exact-amount match has no delta worth
// showing rather than a "+$0.00" pill. Shared by the Pending-review Suggested
// Match column and the Matched drawer so the two can't drift.
export const formatDelta = (raw: string | null): DeltaDisplay | null => {
  if (raw === null) return null;
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount === 0) return null;
  const absolute = formatNumber(Math.abs(amount), "currency");
  const isNegative = amount < 0;
  return {
    display: `${isNegative ? "-" : "+"}${absolute}`,
    kind: isNegative ? "warn" : "success",
  };
};
